import { Director } from 'contexttrees';
import { loadOrCreateProjectFromIndexedDB, saveProjectToIndexedDB } from 'contexttrees/persistence-indexeddb';
import { idbGet, idbPut, STORES } from './database';

/**
 * ContextTrees persistence lives in its *own* IndexedDB database (`contexttrees`, store `projects`,
 * created and versioned by the package's own adapter) — deliberately decoupled from `token-lean-ide`
 * and its `DB_VERSION` upgrades. Nothing here adds a store to, or bumps the version of, that database.
 *
 * The only thing we keep on our side is a lightweight *registry* of project metadata, because the
 * ContextTrees adapter is get/put-by-known-projectId only: it has no way to enumerate what's stored.
 * The registry is one JSON array under a single key in the existing `settings` key->value store, so
 * it needs no new object store either. Heavy snapshot data never touches it.
 */
const KEY_PROJECTS = 'contextTreesProjects';
const KEY_ACTIVE_PROJECT = 'activeContextTreesProjectId';

export interface ContextTreesProjectMeta {
  /** Also used as the ContextTrees persistence-indexeddb `projectId`. */
  id: string;
  name: string;
  /** The one DesignMap node every session scopes to, until a roadmap-tree UI exists to pick a branch. */
  rootNodeId: string;
  createdAt: number;
  updatedAt: number;
}

export async function listContextTreesProjects(): Promise<ContextTreesProjectMeta[]> {
  return (await idbGet<ContextTreesProjectMeta[]>(STORES.settings, KEY_PROJECTS)) ?? [];
}

async function saveContextTreesProjects(projects: ContextTreesProjectMeta[]): Promise<void> {
  await idbPut(STORES.settings, projects, KEY_PROJECTS);
}

/**
 * Mints a project: an empty Director with a single root design-map node, persisted to the
 * ContextTrees database under a fresh id, plus a registry entry pointing at both.
 */
export async function createContextTreesProject(name: string): Promise<ContextTreesProjectMeta> {
  const id = crypto.randomUUID();
  const director = new Director();
  const rootNode = director.designMap.addNode('project', name, null, { status: 'planned' });

  // Snapshot first: a registry entry pointing at a project that failed to save would be a dangling
  // reference, whereas an orphaned snapshot with no registry entry is merely unreferenced.
  await saveProjectToIndexedDB(director, { projectId: id });

  const now = Date.now();
  const meta: ContextTreesProjectMeta = { id, name, rootNodeId: rootNode.id, createdAt: now, updatedAt: now };
  const projects = await listContextTreesProjects();
  await saveContextTreesProjects([...projects, meta]);
  return meta;
}

export async function getActiveContextTreesProjectId(): Promise<string | undefined> {
  return idbGet<string>(STORES.settings, KEY_ACTIVE_PROJECT);
}

export async function setActiveContextTreesProjectId(id: string | undefined): Promise<void> {
  await idbPut(STORES.settings, id, KEY_ACTIVE_PROJECT);
}

/** Returns a fresh empty Director if nothing is stored under `projectId` yet — never throws on first run. */
export async function loadContextTreesDirector(projectId: string): Promise<Director> {
  return loadOrCreateProjectFromIndexedDB({ projectId });
}

export async function saveContextTreesDirector(projectId: string, director: Director): Promise<void> {
  await saveProjectToIndexedDB(director, { projectId });
}
